import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  selectIsAuthenticated,
  selectIsLoading,
  selectUser,
  selectAuthType,
} from '../../redux/slices/authSlice';
import CardLoader from 'wrapper/card-loader';
import { buildAppPath, getCurrentOrgSlug, getTenantScopedStorageKey } from '../../utils/tenant';
import { landingPathForRole } from '../../utils/roles';
import useAuthBootstrap from '../../hooks/useAuthBootstrap';

/**
 * PlatformProtectedRoute - Specialized protection for platform admin routes.
 * Ensures only authenticated platform administrators can access nested routes.
 */
const PlatformProtectedRoute = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const authType = useSelector(selectAuthType);
  const { authLoaded } = useAuthBootstrap();

  if (!authLoaded || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <CardLoader />
      </div>
    );
  }

  const orgSlug = getCurrentOrgSlug();
  const redirectKey = getTenantScopedStorageKey('redirectAfterLogin', orgSlug);
  const targetPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;

  if (!isAuthenticated || !user) {
    localStorage.setItem(redirectKey, targetPath);
    sessionStorage.setItem('redirectAfterLogin', targetPath);
    return <Navigate to={buildAppPath('/platform/login')} replace />;
  }

  const rawRole = String(user.originalRole || user.role || '').toLowerCase();
  const isPlatformAdmin =
    rawRole.includes('platform') ||
    rawRole.includes('super_admin') ||
    (authType && authType.toLowerCase() === 'platform');

  if (!isPlatformAdmin) {
    const fallbackPath = landingPathForRole(user.originalRole || user.role);
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

export default PlatformProtectedRoute;
